// src/pages/Dashboard.jsx
import React, { useState, useEffect } from "react";
import { DailyCheckIn, User } from "@/api/entities";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import {
  BookOpen,
  Dumbbell,
  Heart,
  TrendingUp,
  Calculator,
  Bug as BugIcon,
  BellRing,
  DownloadCloud,
  Settings, // icona per il tile admin
  X,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import BugReportModal from "@/components/BugReportModal";
import PushSetupPanel from "@/components/PushSetupPanel";
import { isAdminCached, isAdmin } from "@/lib/admin"; // ⬅️ NOVITÀ
import ExtrasCard from "@/components/extras/ExtrasCard"; // ⬅️ AGGIUNTO

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [todayCheckIn, setTodayCheckIn] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // admin state (hide/show tile)
  const [adminOk, setAdminOk] = useState(isAdminCached());
  useEffect(() => {
    // conferma lato server senza bloccare il render
    isAdmin().then(setAdminOk).catch(() => setAdminOk(false));
  }, []);

  // Bug modal
  const [showBug, setShowBug] = useState(false);

  // Push notifications modal
  const [showPushPanel, setShowPushPanel] = useState(false);

  // Install (A2HS)
  const [installEvt, setInstallEvt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);

  const isIOS =
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  useEffect(() => {
    loadDashboardData();

    // Detect display-mode standalone (PWA già installata)
    const standalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Intercetta il prompt di installazione (Android/Chrome)
    const onBIP = (e) => {
      e.preventDefault();
      setInstallEvt(e);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    const onInstalled = () => {
      setInstallEvt(null);
      setIsStandalone(true);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      const currentUser = await User.me();

      const today = new Date().toISOString().split("T")[0];
      const checkIns = await DailyCheckIn.filter({
        created_by: currentUser.email,
        date: today,
      });

      const completedCheckIn = checkIns.find(
        (c) => c.q1_feeling && c.q2_motivation && c.q3_breathing
      );

      setUser(currentUser);
      setTodayCheckIn(completedCheckIn || null);
    } catch (error) {
      console.error("Errore caricamento bacheca:", error);
    } finally {
      setIsLoading(false);
    }
  };

  async function handleInstallClick() {
    if (installEvt && !isStandalone) {
      try {
        installEvt.prompt();
        await installEvt.userChoice;
      } catch (e) {
        console.warn("Install prompt chiuso/errore:", e);
      } finally {
        setInstallEvt(null);
      }
      return;
    }
    // Fallback: istruzioni (iOS/altro)
    setShowInstallHelp(true);
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const userName = user?.full_name?.split(" ")[0] || "Utente";

  // Azioni rapide (non di navigazione)
  const quickActions = [
    {
      title: "Attiva notifiche",
      description: "Ricevi promemoria per esercizi e check-in",
      icon: BellRing,
      onClick: () => setShowPushPanel(true),
      gradient: "from-amber-500 to-orange-600",
      bgGradient: "from-amber-50 to-orange-100",
      disabled: false,
    },
    {
      title: isStandalone ? "App installata" : "Installa l’app",
      description: isStandalone
        ? "È già nella schermata Home"
        : installEvt
        ? "Tocca per installare subito"
        : isIOS
        ? "Safari → Condividi → Aggiungi a Home"
        : "Menu browser → Aggiungi a Home",
      icon: DownloadCloud,
      onClick: handleInstallClick,
      gradient: "from-teal-500 to-cyan-600",
      bgGradient: "from-teal-50 to-cyan-100",
      disabled: isStandalone,
    },
  ];

  // Voci menu (navigazione) + Segnala bug + (opzionale) tile admin
  const menuItems = [
    {
      title: "Sfoglia Lezioni",
      description: "Accedi alle tue lezioni",
      icon: BookOpen,
      url: createPageUrl("Lessons"),
      gradient: "from-blue-500 to-blue-600",
      bgGradient: "from-blue-50 to-blue-100",
    },
    {
      title: "Esercizi Potenzianti",
      description: "Pratica gli esercizi quotidiani",
      icon: Dumbbell,
      url: createPageUrl("Exercises"),
      gradient: "from-purple-500 to-purple-600",
      bgGradient: "from-purple-50 to-purple-100",
    },
    {
      title: "Check-in Giornaliero",
      description: todayCheckIn ? "✓ Completato oggi" : "Registra la tua giornata",
      icon: Heart,
      url: createPageUrl("CheckIn"),
      gradient: "from-pink-500 to-pink-600",
      bgGradient: "from-pink-50 to-pink-100",
    },
    {
      title: "Calcolatore Risparmi",
      description: "Scopri quanto hai risparmiato",
      icon: Calculator,
      url: createPageUrl("Calculator"),
      gradient: "from-green-500 to-green-600",
      bgGradient: "from-green-50 to-green-100",
    },
    {
      title: "Vedi Progressi",
      description: "Monitora i tuoi risultati",
      icon: TrendingUp,
      url: createPageUrl("Progress"),
      gradient: "from-indigo-500 to-indigo-600",
      bgGradient: "from-indigo-50 to-indigo-100",
    },

    // Tile admin: visibile solo se adminOk === true
    ...(adminOk
      ? [
          {
            title: "Gestisci Contenuto",
            description: "Area riservata",
            icon: Settings,
            url: "/adminlessons", // usa il path della tua route admin
            gradient: "from-amber-500 to-amber-600",
            bgGradient: "from-amber-50 to-amber-100",
          },
        ]
      : []),

    {
      title: "Segnala un bug",
      description: "Aiutaci a migliorare l’app",
      icon: BugIcon,
      action: () => setShowBug(true),
      gradient: "from-rose-500 to-rose-600",
      bgGradient: "from-rose-50 to-rose-100",
    },
  ];

  return (
    <div className="min-h-screen p-4 md:p-6">
      <div className="max-w-md mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-3"
        >
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Bentornato,{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {userName}
            </span>
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            Scegli di cambiare e{" "}
            <span className="font-semibold text-blue-600">respirare bene</span>
          </p>
        </motion.div>

        {/* Azioni rapide */}
        <div className="grid grid-cols-2 gap-4">
          {quickActions.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <button
                type="button"
                onClick={item.onClick}
                disabled={item.disabled}
                className={`w-full h-32 text-left rounded-xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 ${
                  item.disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"
                }`}
              >
                <Card className={`h-full bg-gradient-to-br ${item.bgGradient} border-0`}>
                  <CardContent className="p-4 h-full flex flex-col items-center justify-center text-center space-y-2">
                    <div
                      className={`w-10 h-10 rounded-2xl bg-gradient-to-r ${item.gradient} flex items-center justify-center shadow-md`}
                    >
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 leading-tight">
                      {item.title}
                    </h3>
                    <p className="text-xs text-gray-600 leading-tight">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </button>
            </motion.div>
          ))}
        </div>

        {/* Menu principale */}
        <div className="grid grid-cols-2 gap-4">
          {menuItems.map((item, index) => {
            const TileInner = (
              <Card
                className={`h-32 bg-gradient-to-br ${item.bgGradient} border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer`}
              >
                <CardContent className="p-4 h-full flex flex-col items-center justify-center text-center space-y-2">
                  <div
                    className={`w-10 h-10 rounded-2xl bg-gradient-to-r ${item.gradient} flex items-center justify-center shadow-md`}
                  >
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-800 leading-tight">
                    {item.title}
                  </h3>
                  <p className="text-xs text-gray-600 leading-tight">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            );

            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + index * 0.05 }}
              >
                {item.url ? (
                  <Link to={item.url}>{TileInner}</Link>
                ) : (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={item.action}
                    onKeyDown={(e) => e.key === "Enter" && item.action?.()}
                  >
                    {TileInner}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* ⬇️ Contenuti extra */}
        <ExtrasCard max={4} />
      </div>

      {/* Modale segnalazione bug */}
      {showBug && <BugReportModal open={showBug} onClose={() => setShowBug(false)} />}

      {/* Modale: Attiva notifiche */}
      {showPushPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="text-lg font-semibold">Attiva notifiche</h3>
              <button
                className="p-2 rounded-md hover:bg-gray-100"
                onClick={() => setShowPushPanel(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              <PushSetupPanel />
            </div>
            <div className="px-5 pb-4">
              <Button variant="outline" className="w-full" onClick={() => setShowPushPanel(false)}>
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modale: Istruzioni installazione / fallback */}
      {showInstallHelp && !isStandalone && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="text-lg font-semibold">Installa l’app</h3>
              <button
                className="p-2 rounded-md hover:bg-gray-100"
                onClick={() => setShowInstallHelp(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              {isIOS ? (
                <>
                  <p>
                    Su iPhone/iPad apri il sito in <b>Safari</b>, tocca <b>Condividi</b> (icona con
                    freccia in su) e poi <b>Aggiungi a Home</b>.
                  </p>
                  <p>
                    Se stai usando un altro browser (es. Chrome), tocca <b>Apri in Safari</b> e
                    segui i passaggi sopra.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Il tuo browser non ha mostrato il prompt automatico. Apri il <b>menu del
                    browser</b> e scegli <b>Aggiungi a schermata Home / Installa app</b>.
                  </p>
                  <p className="text-xs text-gray-500">
                    Suggerimento: su Android/Chrome l’avviso compare spesso dopo 30s di utilizzo o
                    alla seconda visita.
                  </p>
                </>
              )}
            </div>
            <div className="px-5 pb-4">
              <Button className="w-full" onClick={() => setShowInstallHelp(false)}>
                Ok, ho capito
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
